import React, { useEffect, useState, useRef } from 'react';
import { TabStrip } from '@progress/kendo-react-layout';
import isNil from 'lodash/isNil';
import { useHistory, useLocation, useParams } from 'react-router-dom';

const defaultValue = [];

const TabBar = ({
  children,
  selected,
  onSelect,
  tabsKeys = defaultValue,
  // Routing works when tab is the last param only
  routeTabKey = null,
  useHashRouting = false,
  ...props
}) => {
  const params = useParams();

  // Use Tab search param from the URL if routeTabKey is passed
  const location = useLocation();
  const history = useHistory();

  // Parse hash properly by removing the # character if present
  const parsedHash = location.hash ? location.hash.substring(1) : null;
  const tabParam = useHashRouting ? parsedHash : routeTabKey ? params[routeTabKey] : null;

  const tabIndex = tabsKeys.find((tab) => tab.name === tabParam)?.index;
  const initialSelected = tabIndex > -1 ? tabIndex : selected ?? 0;

  const convertToPhysicalIndex = (logicalIndex) => {
    if (!Array.isArray(children)) {
      return logicalIndex;
    }

    const title = children[logicalIndex]?.props?.title;
    return children.filter((x) => x).findIndex((x) => x.props?.title === title);
  };

  const [innerSelected, setInnerSelected] = useState(convertToPhysicalIndex(initialSelected));

  // Support controlled component
  useEffect(() => {
    if (!isNil(selected)) {
      setInnerSelected(selected);
    }
  }, [selected]);

  useEffect(() => {
    if (tabParam) {
      const tabIndex = tabsKeys.find(({name}) => name === tabParam)?.index;
      const physicalIndex = convertToPhysicalIndex(tabIndex);

      if (tabIndex > -1 && physicalIndex !== innerSelected) {
        if (onSelect) {
          onSelect({ selected: physicalIndex, logicalIndex: tabIndex });
          setInnerSelected(physicalIndex);
        } else {
          setInnerSelected(tabIndex);
        }
      }
    }
  }, [tabParam]);

  // If new tab is selected, update the route if needed
  useEffect(() => {
    if (routeTabKey) {
      const tab = children.filter((x) => x)[innerSelected];
      const logicalIndex = children.findIndex((x) => x == tab);
      const tabName = tabsKeys.find((x) => x.index === logicalIndex)?.name;

      if (tabName && tabName !== tabParam) {
        if (isNil(tabParam)) {
          if (useHashRouting) {
            history.replace(`${location.pathname}#${tabName}`);
          } else {
            history.replace(`${location.pathname}/${tabName}`);
          }
        } else {
          if(useHashRouting) {
            // Update hash - fix the hash replacement logic
            const baseUrl = location.pathname.split('#')[0] || location.pathname;
            history.replace(`${baseUrl}#${tabName}`);
          } else {
            const newUrl = location.pathname.replace(/\/[^/]*$/, `/${tabName}`);
            history.replace(newUrl);
          }
        }
      }
    }
  }, [innerSelected]);

  // Component handlers
  const handleSelect = ({ selected: newSelected }) => {
    const childrenSelected = children.filter((x) => x)[newSelected];
    const logicalIndex = children.findIndex((x) => x == childrenSelected);

    if (onSelect) {
      onSelect({ selected: newSelected, logicalIndex });
      setInnerSelected(newSelected);
    } else {
      setInnerSelected(newSelected);
    }
  };

  // To make history button available
  useEffect(() => {
    const historyButtonEls = document.getElementsByClassName('history-tab-button');
    if (historyButtonEls) {
      [...historyButtonEls].forEach((historyButtonEl) => {
        const parentEl = historyButtonEl.parentElement.parentElement;
        if (parentEl) {
          parentEl.style.opacity = 1;
          parentEl.style.pointerEvents = 'all';
          parentEl.style.filter = 'none';
        }
      });
    }
  }, []);

  return (
    <TabStrip
      animation
      keepTabsMounted={false}
      selected={innerSelected}
      onSelect={handleSelect}
      tabPosition="top"
      {...props}
    >
      {children}
    </TabStrip>
  );
};

export default TabBar;
